/**
 * Premium — the memory story (PPL-352).
 * Route: /premium
 *
 * Ports the design's five states (design project pplkpr — premium-web.jsx +
 * premium-kit.jsx): default sell page, at-cap, trialing (PPL-357/358), lapsed,
 * and premium member (manage card, no sell).
 *
 * Every contextual value renders from live data: prices from getPricing
 * (global_settings/webPricing, $9.99/$59.99 fallback pair — PPL-356), the
 * savings % computed from those two numbers, remembered/paused counts and the
 * free limit / memory window from getRememberedPeopleQuotaEurW, trial day and
 * length from the user doc's trialStartedAt/trialEndsAt, the manage card from
 * the user's real subscription. The locked copy (hero lines, tier
 * rows, FAQ) lives in ../types/premium, with the canvas numbers kept only as
 * display defaults while nothing is served.
 */

import React, { useEffect, useRef, useState } from 'react';
import { capture } from '../services/posthog';
import {
  MonetizationEvents,
  PaywallSource,
  coercePaywallGate,
  coercePaywallSource,
} from '../services/monetizationEvents';
import { ArrowLeft, Loader, LogIn } from 'react-feather';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import SEO from '../components/SEO';
import {
  getPremiumStatus,
  getPricing,
  DEFAULT_USD_MONTHLY,
  DEFAULT_USD_YEARLY,
} from '../services/premiumService';
import { createCheckout, cancelSubscription } from '../services/subscriptionService';
import { fetchRememberQuota, type RememberQuota } from '../services/rememberService';
import { getVoiceChatQuotaEurW } from '../firebaseConfig';
import { Moon } from '../components/remember/glyphs';
import {
  PREM_HERO,
  heroSub,
  premTierRows,
  premFaqs,
  PREM_TABLE_FOOT,
  pricingHighlights,
  voiceLabels,
  voiceMeterLine,
  type VoiceLabels,
  type VoiceQuotaSnapshot,
  FREE_LIMIT_FALLBACK,
  MEMORY_WINDOW_FALLBACK_DAYS,
  DEFAULT_TRIAL_LENGTH_DAYS,
  atCapBannerTitle,
  atCapBannerBody,
  LAPSED_BANNER_TITLE,
  lapsedBannerBody,
  lapsedHeroSub,
  lapsedProofLine,
  trialPanelRows,
  trialPanelFoot,
  ctaLabel,
} from '../types/premium';
import type {
  MemoryConfig,
  PremiumStatus,
  PremFaqItem,
  TierRow,
  WebPricing,
} from '../types/premium';
import '../styles/modern.css';
import '../styles/premium.css';
import '../styles/premium-memory.css';

type SellState = 'default' | 'atCap' | 'trialing' | 'lapsed';
type PageState = SellState | 'premium';

interface VoiceQuota extends VoiceQuotaSnapshot {
  isPremium: boolean;
  tier: 'free' | 'premium';
  limit: number;
  used: number;
  remaining: number;
  resetDate: string | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Firestore Timestamp | ms | ISO → Date, or null when absent/invalid. */
function toDate(v: any): Date | null {
  if (!v) return null;
  if (typeof v.toDate === 'function') {
    try { return v.toDate(); } catch { return null; }
  }
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

const fmtUsd = (n: number): string => `$${n.toFixed(2)}`;

export default function Premium() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const uid = user?.uid;

  const [status, setStatus] = useState<PremiumStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState(false);
  const [statusAttempt, setStatusAttempt] = useState(0);
  const [pricing, setPricing] = useState<WebPricing | null>(null);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [plan, setPlan] = useState<'monthly' | 'yearly'>('yearly');
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [quota, setQuota] = useState<RememberQuota | null>(null);
  const [voiceQuota, setVoiceQuota] = useState<VoiceQuota | null>(null);

  /**
   * PPL-351 — this page is a paywall view.
   *
   * A single journey legitimately produces two of these: the at-limit sheet, then
   * this page after clicking through. `source` is what tells them apart, and it
   * arrives as a query param from whichever surface sent the user, defaulting to
   * `premium_screen` for a direct visit. The funnel reads per-person, so the
   * second view does not break the step.
   */

  // Pricing and status load independently: a failed status read must not
  // discard a good price, and vice versa. Pricing failure falls back to the
  // declared pair; status failure is a real error state (see below).
  useEffect(() => {
    getPricing()
      .then(setPricing)
      .catch((err) => console.error('pricing fetch failed:', err))
      .finally(() => setPricingLoading(false));
  }, []);

  useEffect(() => {
    // A uid change is a different person: drop everything derived from the
    // previous one before fetching, so state can never leak across accounts.
    setStatus(null);
    setStatusError(false);
    setPurchasing(false);
    setPurchaseError(null);
    if (!uid) {
      setStatusLoading(false);
      return;
    }
    let stale = false;
    setStatusLoading(true);
    getPremiumStatus(uid)
      .then((s) => { if (!stale) setStatus(s); })
      .catch((err) => {
        console.error('premium status fetch failed:', err);
        if (!stale) {
          setStatusError(true);
          // Keeps the failure measurable (PPL-353 review parity with the app).
          capture(MonetizationEvents.PAYWALL_LOAD_FAILED, {
            source: PaywallSource.PREMIUM_SCREEN,
            referrer_source: coercePaywallSource(searchParams.get('source')),
            gate: coercePaywallGate(searchParams.get('gate')),
          });
        }
      })
      .finally(() => { if (!stale) setStatusLoading(false); });
    return () => { stale = true; };
  }, [uid, statusAttempt]);

  // The remembered-people quota drives the atCap state, the lapsed counts, the
  // trial panel and the served limit/window in the copy. A failure just means
  // the display defaults render.
  useEffect(() => {
    setQuota(null);
    if (!uid) return;
    fetchRememberQuota()
      .then(setQuota)
      .catch((err) => {
        console.error('remember quota fetch failed:', err);
        setQuota(null);
      });
  }, [uid]);

  useEffect(() => {
    setVoiceQuota(null);
    if (!uid) return;
    getVoiceChatQuotaEurW({})
      .then((res) => setVoiceQuota(res.data as VoiceQuota))
      .catch((err) => console.error('voice quota fetch failed:', err));
  }, [uid]);

  // PPL-515: every voice figure on this page comes from the served snapshot,
  // the way the app already did it — web used to hard-code "60 min / mo".
  const voice: VoiceLabels = voiceLabels(voiceQuota);

  const loading = statusLoading || pricingLoading;
  const isPremium = status?.isPremium ?? false;

  /* ─── State machine (design: five states, one story) ─── */

  // PPL-357 — no trialists exist yet, so every field is validated before use.
  const trialEnd = toDate(status?.trialEndsAt);
  const trialStart = toDate(status?.trialStartedAt);
  const trialing =
    !isPremium &&
    status?.trialStatus === 'active' &&
    trialEnd !== null &&
    trialEnd.getTime() > Date.now();
  // The length is derived from the server's own bounds; the 14 constant is
  // only the display fallback for a doc missing trialStartedAt.
  const trialLength = trialing && trialStart && trialEnd && trialEnd.getTime() > trialStart.getTime()
    ? Math.max(1, Math.round((trialEnd.getTime() - trialStart.getTime()) / DAY_MS))
    : DEFAULT_TRIAL_LENGTH_DAYS;
  const trialDay = trialing && trialEnd
    ? Math.min(
        trialLength,
        Math.max(1, trialLength - Math.floor((trialEnd.getTime() - Date.now()) / DAY_MS)),
      )
    : 1;
  const trialEndsLabel = trialEnd
    ? trialEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toLowerCase()
    : '';

  const lapsed = !isPremium && !trialing && !!status?.lapsedAt;

  // Where they came FROM. `source` on this page's own events is always
  // `premium_screen` — one property cannot answer both questions. Coerced,
  // because these arrive from a URL: `/premium?source=whatever` would otherwise
  // mint a bucket that looks like a real surface.
  const referrerSource = coercePaywallSource(searchParams.get('source'));
  const referrerGate = coercePaywallGate(searchParams.get('gate'));

  // The quota is authoritative once it arrives: the limit-sheet referrer may
  // only stand in for it while it is still null (and only for a signed-in
  // visitor — a signed-out one has no cap to be at).
  const fromLimitSheet = referrerSource === PaywallSource.REMEMBERED_LIMIT_SHEET;
  const atCapByQuota =
    quota !== null &&
    !quota.isPremium &&
    quota.limit !== null &&
    quota.remaining !== null &&
    quota.remaining <= 0;
  const atCap =
    !isPremium &&
    !trialing &&
    !lapsed &&
    !!uid &&
    (quota === null ? fromLimitSheet : atCapByQuota);

  const pageState: PageState = isPremium
    ? 'premium'
    : trialing
      ? 'trialing'
      : lapsed
        ? 'lapsed'
        : atCap
          ? 'atCap'
          : 'default';
  const sellState: SellState = pageState === 'premium' ? 'default' : pageState;

  /** Paused ppl = remembered − active. Null = unknown → the copy drops the count. */
  const pausedCount = quota
    ? Math.max(0, quota.rememberedCount - quota.activeCount)
    : null;

  /**
   * The served free limit / memory window, for the copy — PPL-383's
   * `freeLimit`/`freeMemoryWindowDays` first (served to everyone, entitled
   * users included), the per-user fields as the older-payload fallback, the
   * canvas numbers (5, 30) only while nothing has loaded. One config for the
   * whole page: TrialPanel, the tier table and the day-14 FAQ must never
   * state different numbers on one screen.
   */
  const memCfg: MemoryConfig = {
    freeLimit: quota?.freeLimit ?? quota?.limit ?? FREE_LIMIT_FALLBACK,
    windowDays: quota?.freeMemoryWindowDays ?? quota?.memoryWindowDays ?? MEMORY_WINDOW_FALLBACK_DAYS,
  };

  /* ─── Pricing — two live numbers, everything else derived ─── */

  // Fallback for a failed getPricing(), taken from the service's own defaults
  // rather than retyped ($9.99 / $59.99 — the PPL-356 reconciled pair).
  const p: WebPricing = pricing ?? {
    usdMonthly: DEFAULT_USD_MONTHLY,
    usdYearly: DEFAULT_USD_YEARLY,
  };
  const savePct = p.usdMonthly > 0
    ? Math.round((1 - p.usdYearly / (p.usdMonthly * 12)) * 100)
    : 0;
  const priceStr = plan === 'yearly' ? fmtUsd(p.usdYearly) : fmtUsd(p.usdMonthly);
  const priceUnit = plan === 'yearly' ? 'year' : 'month';
  const perMonthEquiv = fmtUsd(p.usdYearly / 12);

  const viewLogged = useRef(false);

  /**
   * PPL-351 — a paywall view, but only when a paywall was actually shown.
   *
   * This page renders a management card with no pricing for existing
   * subscribers, so firing at mount would count every "manage my subscription"
   * visit as a paywall view — inflating step one and depressing the measured
   * view→CTA rate. `status` loads async, so it waits rather than gating at
   * mount — and a FAILED status read proves nothing about the visitor's tier,
   * so it never fires either (the page shows an error state, not a paywall).
   */
  useEffect(() => {
    if (loading || statusError || isPremium || viewLogged.current) return;
    viewLogged.current = true;
    capture(MonetizationEvents.PAYWALL_VIEWED, {
      source: PaywallSource.PREMIUM_SCREEN,
      referrer_source: referrerSource,
      gate: referrerGate,
      // The TIER on offer, not the billing cycle — `billing_cycle` is its own
      // property here and on the server's checkout_started, and one name for two
      // things means no single breakdown spans the funnel.
      tier_shown: 'premium',
      billing_cycle: plan,
      // PPL-357 shipped, so this is now the real answer.
      is_trialing: trialing,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isPremium, statusError]);

  const onSignInCta = () => {
    // A logged-out visitor's ONLY available next step: `handleSubscribe` returns
    // at `!uid` before it can fire anything, so without this the funnel records a
    // view whose step 2 is structurally impossible — permanently depressing the
    // measured view→CTA rate for this surface.
    capture(MonetizationEvents.UPGRADE_CTA_CLICKED, {
      source: PaywallSource.PREMIUM_SCREEN,
      referrer_source: referrerSource,
      gate: referrerGate,
      billing_cycle: plan,
      // Which telling of the story sold it — the closed PaywallSellState set.
      sell_state: sellState,
      is_trialing: trialing,
      requires_signin: true,
    });
  };

  const handleSubscribe = async () => {
    if (!uid) return;
    setPurchasing(true);
    setPurchaseError(null);
    try {
      // Intent. `checkout_started` is NOT fired here — `createWebCheckoutEurW`
      // emits it server-side once the PayFast URL actually exists, so a failed
      // checkout creation is not counted as an intent to pay.
      capture(MonetizationEvents.UPGRADE_CTA_CLICKED, {
        source: PaywallSource.PREMIUM_SCREEN,
        referrer_source: referrerSource,
        gate: referrerGate,
        billing_cycle: plan,
        sell_state: sellState,
        is_trialing: trialing,
      });
      // Store plan choice so callback page knows which billing cycle was selected
      sessionStorage.setItem('pplkpr_checkout_plan', plan);
      const origin = window.location.origin;
      await createCheckout(
        plan,
        `${origin}/premium/callback?status=success`,
        `${origin}/premium/callback?status=cancelled`,
      );
    } catch (err: any) {
      setPurchaseError(err?.message || 'something went wrong. please try again.');
      setPurchasing(false);
    }
  };

  const proof = lapsed ? lapsedProofLine(pausedCount) : null;

  return (
    <div className="premium-page-wrap">
      <SEO
        title="premium | pplkpr"
        description="f8 remembers everyone, forever — a brief before every meeting and a history that never fades."
        path="/premium"
      />

      <div className="premium-content">
        {/* Back */}
        <button className="up-back" onClick={() => navigate(-1)} style={{ marginBottom: 20 }}>
          <ArrowLeft size={14} />
          <span>back</span>
        </button>

        {loading ? (
          <div className="glass-panel" style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>loading...</div>
          </div>
        ) : statusError ? (
          /* A failed status read proves nothing about the visitor's tier —
             showing the sell page to a possibly-premium member would be wrong
             in both directions, so this is a neutral state instead. */
          <div className="glass-panel" style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 16 }}>
              couldn&rsquo;t load your account right now.
            </div>
            <button className="pmx-ghost-pill" onClick={() => setStatusAttempt((a) => a + 1)}>
              try again
            </button>
          </div>
        ) : pageState === 'premium' ? (
          <>
            {/* ─── Premium member — manage, no sell ─── */}
            <div className="pmx-member-intro">
              <div className="premium-pill">
                <F8Sparkle />
                <span>pplkpr premium</span>
              </div>
              <h1 className="premium-headline" style={{ fontSize: 'clamp(34px, 5vw, 44px)' }}>
                you&rsquo;re on premium.
              </h1>
              <p className="premium-hero-sub">
                f8 remembers everyone, forever. nothing to buy here &mdash; this is just where
                your plan lives.
              </p>
            </div>

            <div className="pmx-member-card-wrap">
              <ManageCard
                key={uid}
                status={status}
                onStatusRefresh={async () => {
                  if (uid) setStatus(await getPremiumStatus(uid));
                }}
              />
            </div>

            <div className="premium-section">
              <div className="premium-section-kicker">for the record</div>
              <h2 className="premium-section-title" style={{ marginBottom: 22 }}>everything premium holds</h2>
              <TierTable cfg={memCfg} voice={voice} />
            </div>

            <div className="premium-section">
              <div className="premium-section-kicker">answers</div>
              <h2 className="premium-section-title" style={{ marginBottom: 22 }}>things people ask</h2>
              <Faq cfg={memCfg} trialLength={trialLength} trialing={false} />
            </div>
          </>
        ) : (
          <>
            {/* ─── Sell states: default · atCap · trialing · lapsed ─── */}
            <StateBanner
              state={sellState}
              trialDay={trialDay}
              trialLength={trialLength}
              trialEndsLabel={trialEndsLabel}
              quota={quota}
              pausedCount={pausedCount}
            />

            {/* ─── Hero ─── */}
            <div className="premium-hero-grid">
              <div className="premium-hero-text">
                <div className="premium-pill">
                  <F8Sparkle />
                  <span>pplkpr premium</span>
                </div>

                <h1 className="premium-headline">
                  {PREM_HERO.h1a}<br />
                  <span className="premium-headline-gradient">{PREM_HERO.h1b}</span>
                </h1>

                <p className="premium-hero-sub">
                  {sellState === 'lapsed' ? lapsedHeroSub(pausedCount) : heroSub()}
                </p>

                {proof && (
                  <div className="pmx-proof">
                    <AvatarCluster n={pausedCount ?? 0} dim />
                    <span><b>{proof.strong}</b>{proof.rest}</span>
                  </div>
                )}
              </div>

              {/* Right: pricing card (+ the free voice meter beneath it) */}
              <div>
                <PricingCard
                  plan={plan}
                  setPlan={setPlan}
                  priceStr={priceStr}
                  priceUnit={priceUnit}
                  perMonthEquiv={perMonthEquiv}
                  savePct={savePct}
                  cta={ctaLabel(sellState)}
                  signedIn={!!user}
                  purchasing={purchasing}
                  purchaseError={purchaseError}
                  onSubscribe={handleSubscribe}
                  onSignInCta={onSignInCta}
                  voice={voice}
                />
                {voiceQuota?.tier === 'free' && (
                  <div className="pmx-voice-meter">
                    <span className="pmx-voice-meter-label">voice</span>
                    <span className="pmx-voice-meter-value">
                      {voiceMeterLine(voiceQuota, voiceQuota.used)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {sellState === 'trialing' && <TrialPanel quota={quota} trialLength={trialLength} />}

            {/* ─── Tier table ─── */}
            <div className="premium-section">
              <div className="premium-section-kicker">every line, side by side</div>
              <h2 className="premium-section-title">free vs. premium</h2>
              <TierTable cfg={memCfg} voice={voice} />
            </div>

            {/* ─── FAQ ─── */}
            <div className="premium-section">
              <div className="premium-section-kicker">answers</div>
              <h2 className="premium-section-title" style={{ marginBottom: 22 }}>things people ask</h2>
              <Faq cfg={memCfg} trialLength={trialLength} trialing={sellState === 'trialing'} />
            </div>

            {/* ─── Final CTA ─── */}
            <div className="premium-final-cta">
              <div className="premium-final-cta-glow" />
              <div className="premium-final-cta-text">
                <h3 className="premium-final-cta-title">
                  {sellState === 'lapsed' ? 'pick up where you left off.' : 'f8 remembers everyone.'}
                </h3>
                <p className="premium-final-cta-sub">
                  {priceStr} billed {plan === 'yearly' ? 'yearly' : 'monthly'} &middot; cancel anytime
                </p>
              </div>
              {user ? (
                <button className="premium-final-cta-btn" onClick={handleSubscribe} disabled={purchasing}>
                  <F8Sparkle c="#d946ef" /> {ctaLabel(sellState)}
                </button>
              ) : (
                <Link to="/login" onClick={onSignInCta} className="premium-final-cta-btn" style={{ textDecoration: 'none' }}>
                  <LogIn size={15} /> sign in
                </Link>
              )}
            </div>
          </>
        )}

        {/* ─── Trust strip ─── */}
        {!loading && !statusError && (
          <div className="premium-trust-strip">
            <TrustItem icon="shield" label="cancel anytime" />
            <TrustItem icon="lock" label="encrypted in transit & at rest" />
            <TrustItem icon="card" label="apple pay · google pay · card" />
            <TrustItem icon="bolt" label="instant access" />
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── State banners (atCap · trialing · lapsed) ───────────── */

function StateBanner({
  state,
  trialDay,
  trialLength,
  trialEndsLabel,
  quota,
  pausedCount,
}: {
  state: SellState;
  trialDay: number;
  trialLength: number;
  trialEndsLabel: string;
  quota: RememberQuota | null;
  pausedCount: number | null;
}) {
  if (state === 'atCap') {
    return (
      <div className="pmx-banner pmx-banner--gold">
        <span className="pmx-banner-icon">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5L12 3Z" fill="var(--pp-gold)" />
          </svg>
        </span>
        <div style={{ minWidth: 0 }}>
          <div className="pmx-banner-title">{atCapBannerTitle(quota?.limit ?? null)}</div>
          <div className="pmx-banner-body">{atCapBannerBody(quota?.limit ?? null)}</div>
        </div>
      </div>
    );
  }
  if (state === 'trialing') {
    return (
      <div className="pmx-banner pmx-banner--trial">
        <div className="pmx-trial-head">
          <div className="pmx-trial-day">
            day {trialDay} of {trialLength} &middot; <span className="pmx-trial-on">full memory is on</span>
          </div>
          {trialEndsLabel && <div className="pmx-trial-ends">trial ends {trialEndsLabel}</div>}
        </div>
        <div className="pmx-trial-track">
          <div
            className="pmx-trial-fill"
            style={{ width: `${Math.round((trialDay / trialLength) * 100)}%` }}
          />
        </div>
        <div className="pmx-trial-note">
          everything below is yours right now. at day {trialLength}, f8 pauses &mdash; never deletes.
        </div>
      </div>
    );
  }
  if (state === 'lapsed') {
    return (
      <div className="pmx-banner pmx-banner--gold">
        <span className="pmx-banner-icon"><Moon size={15} /></span>
        <div style={{ minWidth: 0 }}>
          <div className="pmx-banner-title">{LAPSED_BANNER_TITLE}</div>
          <div className="pmx-banner-body">
            {lapsedBannerBody(quota?.limit ?? null, quota?.memoryWindowDays ?? null, pausedCount)}
          </div>
        </div>
      </div>
    );
  }
  return null;
}

/* ─── "at day N, concretely" (PPL-358 — countdown, not doom) ─── */

function TrialPanel({ quota, trialLength }: { quota: RememberQuota | null; trialLength: number }) {
  // PPL-383 — the FREE-tier numbers, served to everyone. A trialist's own
  // `limit`/`memoryWindowDays` are null (they're entitled right now), and this
  // panel's whole job is to name what day 14 takes away. The per-user fields
  // remain as the older-payload fallback so the panel never knows less than it
  // used to.
  const rows = trialPanelRows(
    quota?.freeLimit ?? quota?.limit ?? null,
    quota?.freeMemoryWindowDays ?? quota?.memoryWindowDays ?? null,
    quota?.rememberedCount ?? null,
  );
  return (
    <div className="pmx-trial-panel">
      <div className="pmx-trial-kicker">at day {trialLength}, concretely</div>
      <div className="pmx-trial-rows">
        {rows.map((r) => (
          <div key={r.title} className="pmx-trial-row">
            <span className="pmx-trial-moon"><Moon size={9} /></span>
            <div className="pmx-trial-row-main">
              <div className="pmx-trial-row-title-wrap">
                <span className="pmx-trial-row-title">{r.title}</span>
                {r.avatars ? <AvatarCluster n={r.avatars} dim small /> : null}
              </div>
              <div className="pmx-trial-row-sub">{r.sub}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="pmx-trial-foot">{trialPanelFoot(trialLength)}</div>
    </div>
  );
}

/* ─── Pricing card — every string derives from the two live prices ─── */

function PricingCard({
  plan,
  setPlan,
  priceStr,
  priceUnit,
  perMonthEquiv,
  savePct,
  cta,
  signedIn,
  purchasing,
  purchaseError,
  onSubscribe,
  onSignInCta,
  voice,
}: {
  plan: 'monthly' | 'yearly';
  setPlan: (p: 'monthly' | 'yearly') => void;
  priceStr: string;
  priceUnit: string;
  perMonthEquiv: string;
  savePct: number;
  cta: string;
  signedIn: boolean;
  purchasing: boolean;
  purchaseError: string | null;
  onSubscribe: () => void;
  onSignInCta: () => void;
  voice: VoiceLabels;
}) {
  const sub = plan === 'yearly'
    ? `billed yearly · works out to ${perMonthEquiv}/month · save ${savePct}%`
    : 'billed monthly';
  return (
    <div className="premium-pricing-card">
      {/* Plan switcher */}
      <div className="premium-plan-switcher">
        {(['monthly', 'yearly'] as const).map(opt => (
          <button
            key={opt}
            className={`premium-plan-btn ${plan === opt ? 'active' : ''}`}
            onClick={() => setPlan(opt)}
          >
            <span>{opt}</span>
            {opt === 'yearly' && (
              <span className={`premium-save-badge ${plan === 'yearly' ? 'active' : ''}`}>
                save {savePct}%
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Price */}
      <div className="premium-price-display">
        <span className="premium-price-amount">{priceStr}</span>
        <span className="premium-price-period">/ {priceUnit}</span>
      </div>
      <div className="premium-price-sub">{sub}</div>

      {/* CTA */}
      {signedIn ? (
        <>
          {purchaseError && (
            <div style={{ marginTop: 16, padding: 10, borderRadius: 8, background: 'rgba(255,107,107,0.1)', color: '#ff6b6b', fontSize: 13 }}>
              {purchaseError}
            </div>
          )}
          <button
            className="premium-cta-btn"
            onClick={onSubscribe}
            disabled={purchasing}
          >
            {purchasing ? (
              <><Loader size={15} className="spin" /> redirecting...</>
            ) : (
              <><F8Sparkle /> {cta}</>
            )}
          </button>
        </>
      ) : (
        <Link to="/login" onClick={onSignInCta} className="premium-cta-btn" style={{ textDecoration: 'none' }}>
          <LogIn size={15} /> sign in to subscribe
        </Link>
      )}

      <div className="premium-microcopy">
        cancel anytime. you won&rsquo;t be charged until you confirm.
      </div>

      {/* Inline highlights */}
      <div className="premium-highlights">
        {pricingHighlights(voice).map((it: string) => (
          <div key={it} className="premium-highlight-item">
            <span className="premium-highlight-check">
              <CheckMark />
            </span>
            <span>{it}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


function TierCell({ v, side, hi }: { v: string; side: 'free' | 'prem'; hi?: boolean }) {
  if (v === '✓') {
    return (
      <span className={`pmx-cell-check pmx-cell-check--${side}`}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
          <path d="M5 12l5 5L20 7" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  if (v === '—') return <span className="pmx-cell-dash">&mdash;</span>;
  const cls = side === 'prem'
    ? (hi ? 'pmx-cell-text pmx-cell-text--prem-hi' : 'pmx-cell-text pmx-cell-text--prem')
    : 'pmx-cell-text pmx-cell-text--free';
  return <span className={cls}>{v}</span>;
}

function TierTable({ cfg, voice }: { cfg: MemoryConfig; voice: VoiceLabels }) {
  return (
    <div className="pmx-table">
      <div className="pmx-table-head">
        <div className="pmx-th">feature</div>
        <div className="pmx-th pmx-th--center">free</div>
        <div className="pmx-th--prem">
          <F8Sparkle size={11} />
          <span className="pmx-th-label pmx-grad-text">premium</span>
        </div>
      </div>
      {premTierRows(cfg, voice).map((r: TierRow) => (
        <div
          key={r.f}
          className={`pmx-row${r.hi ? ' pmx-row--hi' : ''}${r.same ? ' pmx-row--same' : ''}`}
        >
          <div className="pmx-row-f">{r.f}</div>
          <div className="pmx-cell"><TierCell v={r.free} side="free" /></div>
          <div className="pmx-cell"><TierCell v={r.prem} side="prem" hi={r.hi} /></div>
        </div>
      ))}
      <div className="pmx-table-foot">{PREM_TABLE_FOOT}</div>
    </div>
  );
}

/* ─── FAQ — the downgrade answer is styled like it matters ─── */

function Faq({ cfg, trialLength, trialing }: { cfg: MemoryConfig; trialLength: number; trialing: boolean }) {
  const rows = premFaqs(cfg, trialLength).filter((f) => !f.trialOnly || trialing);
  return (
    <div className="premium-faq">
      {rows.map((f, i) => (
        <FaqRow
          key={f.q}
          f={f}
          last={i === rows.length - 1}
          defaultOpen={!!f.open || (trialing && !!f.trialOnly)}
        />
      ))}
    </div>
  );
}

function FaqRow({ f, last, defaultOpen }: { f: PremFaqItem; last: boolean; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="premium-faq-item" style={{ borderBottom: last ? 0 : undefined }}>
      <button className="premium-faq-btn" onClick={() => setOpen(!open)}>
        <span className="premium-faq-q">{f.q}</span>
        <span className={`premium-faq-toggle ${open ? 'open' : ''}`}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"/>
          </svg>
        </span>
      </button>
      {open && (
        <div className="premium-faq-a">
          {f.hero && (
            <div className="pmx-faq-hero">
              <span className="pmx-grad-text">{f.hero}</span>
            </div>
          )}
          {f.a}
        </div>
      )}
    </div>
  );
}

/* ─── Premium-member manage card — no sell ─────────────────── */

function ManageCard({
  status,
  onStatusRefresh,
}: {
  status: PremiumStatus | null;
  onStatusRefresh: () => Promise<void>;
}) {
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelResult, setCancelResult] = useState<{ activeUntil: string } | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // The server writes lowercase app_store / play_store; older docs carried
  // uppercase — normalise before comparing or app subs lose their store note.
  const storeNorm = (status?.store || '').toLowerCase();
  const isWebSub = storeNorm === 'stripe' || storeNorm === 'web';
  const isAppSub = storeNorm === 'app_store' || storeNorm === 'play_store';

  // Cancelled is a server-known state (planStatus/cancelledAt survive a
  // reload), with the local cancel result covering the moment before the
  // backend has caught up.
  const cancelled = status?.planStatus === 'cancelled' || !!cancelResult;

  const untilDate = toDate(cancelResult?.activeUntil ?? status?.expiry);
  const untilLabel = untilDate
    ? untilDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).toLowerCase()
    : null;
  const planType = status?.planType ?? null;

  const handleCancel = async () => {
    setCancelling(true);
    setCancelError(null);
    let succeeded = false;
    try {
      const result = await cancelSubscription();
      setCancelResult(result);
      setCancelConfirm(false);
      succeeded = true;
    } catch (err: any) {
      setCancelError(err?.message || 'failed to cancel. please try again.');
    } finally {
      setCancelling(false);
    }
    if (succeeded) {
      // The cancel itself worked; a failed refresh must not read as a failed
      // cancel, so it only logs.
      try {
        await onStatusRefresh();
      } catch (err) {
        console.error('post-cancel status refresh failed:', err);
      }
    }
  };

  return (
    <div className="pmx-manage">
      <div className="pmx-manage-head">
        <F8Sparkle size={13} />
        <span className="pmx-manage-title">your plan</span>
        <span className={`pmx-manage-badge${cancelled ? ' pmx-manage-badge--gold' : ''}`}>
          {cancelled ? 'cancelled' : 'active'}
        </span>
      </div>

      {planType && (
        <div className="pmx-manage-row">
          <span className="pmx-manage-k">plan</span>
          <span className="pmx-manage-v">{planType}</span>
        </div>
      )}
      {untilLabel && (
        <div className="pmx-manage-row">
          <span className="pmx-manage-k">{cancelled ? 'premium until' : 'renews'}</span>
          <span className="pmx-manage-v">{untilLabel}</span>
        </div>
      )}
      {isWebSub && (
        <div className="pmx-manage-row">
          <span className="pmx-manage-k">payment</span>
          <span className="pmx-manage-v">on file</span>
        </div>
      )}

      {cancelError && (
        <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: 'rgba(255,107,107,0.1)', color: '#ff6b6b', fontSize: 12.5 }}>
          {cancelError}
        </div>
      )}

      {isWebSub && !cancelled && (
        !cancelConfirm ? (
          <div className="pmx-manage-actions">
            <button className="pmx-ghost-pill" onClick={() => setCancelConfirm(true)}>
              cancel plan
            </button>
          </div>
        ) : (
          <div className="pmx-manage-actions">
            <button className="pmx-ghost-pill" onClick={() => setCancelConfirm(false)} disabled={cancelling}>
              keep premium
            </button>
            <button className="pmx-ghost-pill pmx-ghost-pill--danger" onClick={handleCancel} disabled={cancelling}>
              {cancelling ? (
                <><Loader size={11} className="spin" /> cancelling...</>
              ) : (
                'yes, cancel'
              )}
            </button>
          </div>
        )
      )}

      {isAppSub && (
        <div className="pmx-manage-note">
          manage your plan through the {storeNorm === 'app_store' ? 'App Store' : 'Google Play Store'},
          where you subscribed.
        </div>
      )}

      {isWebSub && untilLabel && (
        <div className="pmx-manage-note">
          {cancelled
            ? <>your premium stays until {untilLabel}. after that, f8 pauses &mdash; it never deletes.</>
            : <>canceling keeps premium until {untilLabel}. after that, f8 pauses &mdash; it never deletes.</>}
        </div>
      )}
    </div>
  );
}

/* ─── Atoms ─────────────────────────────────────────────────── */

function F8Sparkle({ size = 12, c = '#fff' }: { size?: number; c?: string }) {
  return (
    <span className="f8-sparkle" style={{ width: size + 6, height: size + 6 }}>
      <svg width={size - 2} height={size - 2} viewBox="0 0 24 24" fill="none">
        <path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5L12 3Z" fill={c}/>
      </svg>
    </span>
  );
}

function CheckMark() {
  return (
    <svg width="9" height="9" viewBox="0 0 24 24" fill="none">
      <path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Decorative avatar cluster (kit PremAvatars) — counts only, never identities. */
function AvatarCluster({ n, dim = false, small = false }: { n: number; dim?: boolean; small?: boolean }) {
  const hues = [320, 280, 30, 200, 160, 250, 90, 340];
  const shown = Math.min(n, 5);
  return (
    <span className={`pmx-avatars${dim ? ' pmx-avatars--dim' : ''}${small ? ' pmx-avatars--sm' : ''}`}>
      {hues.slice(0, shown).map((h, i) => (
        <span
          key={i}
          className="pmx-avatar"
          style={{ background: `linear-gradient(135deg, hsl(${h},55%,50%), hsl(${h + 30},55%,38%))` }}
        />
      ))}
      {n > 5 && <span className="pmx-avatar pmx-avatar--more">+{n - 5}</span>}
    </span>
  );
}

function TrustItem({ icon, label }: { icon: string; label: string }) {
  const paths: Record<string, React.ReactNode> = {
    shield: <path d="M12 3l8 3v6c0 5-4 8-8 9-4-1-8-4-8-9V6l8-3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>,
    lock: <><rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="1.6"/><path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.6"/></>,
    card: <><rect x="3" y="6" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.6"/><path d="M3 10h18" stroke="currentColor" strokeWidth="1.6"/></>,
    bolt: <path d="M13 3 5 14h6l-2 7 8-11h-6l2-7Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>,
  };
  return (
    <div className="premium-trust-item">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">{paths[icon]}</svg>
      <span>{label}</span>
    </div>
  );
}
